import React, { memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { useViewport } from '../../../shared/hooks/useViewport';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { Customer } from '../../../types/backend';
import { useEscalationLevel } from '../../customer/context/EscalationContext';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import OperationsAccountView from './OperationsAccountView';
import AccountFormFields from './AccountFormFields';
import AccountDetailTabs from './AccountDetailTabs';
import { useAccountForm } from '../hooks/useAccountForm';
import { useAccountPanes } from '../hooks/useAccountPanes';

interface AwsAccountManagementModalProps {
  isOpen: boolean;
  customer: Customer;
  onClose: () => void;
}

const AwsAccountManagementModal = memo<AwsAccountManagementModalProps>(({
  isOpen,
  customer,
  onClose
}) => {
  // Below `md` the dialog is the whole screen and shows the list or the open
  // account's detail, never both stacked; from `md` up they sit side by side.
  const { isCompact } = useViewport();
  const { effectiveLevel } = useEscalationLevel();
  const allowWriteConsole = effectiveLevel === 'l2' || effectiveLevel === 'admin';
  const allowAccountDecisions = effectiveLevel === 'admin';
  // Every account_baseline action is refused while the switch is off, so the
  // onboarding panel (and the flow dialog behind it) and both decisions go too.
  const baselineEnabled = useFeatureSwitch('account_baseline');

  const form = useAccountForm({
    customerId: customer.id, isOpen, baselineEnabled, allowAdminRoles: effectiveLevel === 'admin',
  });
  const {
    detailOpen, setDetailOpen, showOperationsAccount, setShowOperationsAccount, markDeleting,
  } = useAccountPanes({ isOpen, saving: form.saving, accountIds: form.entities.map((account) => account.id) });

  const formFields = (
    <AccountFormFields
      form={form}
      allowWriteConsole={allowWriteConsole}
      allowAccountDecisions={allowAccountDecisions}
      allowAdminRoles={effectiveLevel === 'admin'}
      baselineEnabled={baselineEnabled}
      handleDeleteClick={!form.isNew && form.selectedEntity && effectiveLevel === 'admin' ? form.handleDeleteClick : undefined}
    />
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isCompact}
      PaperProps={{
        sx: isCompact ? undefined : { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <ModalHeader
        icon={CloudIcon}
        title="Manage AWS Accounts"
        onClose={onClose}
        subtitle={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Customer: <strong>{customer.name}</strong>
          </Typography>
        }
      />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* Above both panes, so below `md` a message shows whichever pane is open. */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {form.error && (
            <InlineAlert severity="error" onClose={() => form.setError(null)} sx={{ m: 2, mb: 0, flexShrink: 0 }}>
              {form.error}
            </InlineAlert>
          )}
          {form.successMessage && (
            <InlineAlert severity="success" onClose={() => form.setSuccessMessage(null)} sx={{ m: 2, mb: 0, flexShrink: 0 }}>
              {form.successMessage}
            </InlineAlert>
          )}
          {form.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <MasterDetailLayout
              hasSelection={detailOpen}
              onBack={() => setDetailOpen(false)}
              listWidth={280}
              backLabel="Back to accounts"
              list={
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {customer.operations_account_id && (
                    <>
                      <Box sx={{ p: 2, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          OPERATIONS ACCOUNT
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => {
                          setShowOperationsAccount(true);
                          setDetailOpen(true);
                        }}
                        sx={{
                          mx: 1,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: showOperationsAccount ? 'primary.lighter' : 'transparent',
                          border: 1,
                          borderColor: showOperationsAccount ? 'primary.main' : 'divider',
                          '&:hover': {
                            bgcolor: showOperationsAccount ? 'primary.lighter' : 'action.hover',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon color={showOperationsAccount ? 'primary' : 'action'} fontSize="small" />
                            <Box>
                              <Typography variant="body2" fontWeight={showOperationsAccount ? 600 : 400}>
                                Operations
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {customer.operations_account_id}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <Divider sx={{ mx: 2 }} />
                    </>
                  )}

                  <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      CUSTOMER ACCOUNTS
                    </Typography>
                  </Box>
                  <EntityListPanel
                    entities={form.entities}
                    selectedEntity={showOperationsAccount ? null : form.selectedEntity}
                    isNew={form.isNew}
                    saving={form.saving}
                    config={{
                      getPrimaryText: (account) => account.account_name,
                      getSecondaryText: (account) => account.aws_account_id,
                      getId: (account) => account.id,
                      Icon: CloudIcon
                    }}
                    addButtonText="Add New Account"
                    emptyStateText="No accounts configured"
                    emptyStateHelperText='Click "Add New Account" to get started'
                    onSelect={(account) => {
                      setShowOperationsAccount(false);
                      form.handleSelect(account);
                      setDetailOpen(true);
                    }}
                    onAddNew={() => {
                      setShowOperationsAccount(false);
                      form.handleAddNew();
                      setDetailOpen(true);
                    }}
                  />
                </Box>
              }
              detail={
                showOperationsAccount && customer.operations_account_id ? (
                  <OperationsAccountView customer={customer} allowWriteConsole={allowWriteConsole} />
                ) : (form.selectedEntity || form.isNew) ? (
                  // The pane is the one scroll area for the panel and the form. A form
                  // scrolling on its own beside a tall onboarding panel was left no
                  // height, and Save could not be reached.
                  <Box
                    data-testid="account-detail-pane"
                    sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
                  >
                    {form.selectedEntity && !form.isNew ? (
                      <AccountDetailTabs
                        key={form.selectedEntity.id}
                        customer={customer}
                        form={form}
                        baselineEnabled={baselineEnabled}
                        allowAccountEdit={allowWriteConsole}
                        allowAccountDecisions={allowAccountDecisions}
                        formFields={formFields}
                      />
                    ) : formFields}
                  </Box>
                ) : (
                  <EmptyStateMessage
                    icon={CloudIcon}
                    title="No account selected"
                    description="Select an account from the list or add a new one"
                  />
                )
              }
            />
          )}
        </Box>
      </DialogContent>

      <ModalFooter
        count={form.entities.length}
        label={`account${form.entities.length !== 1 ? 's' : ''} configured`}
        onClose={onClose}
      />

      <DeleteConfirmDialog
        open={form.showDeleteConfirm}
        title="Delete Account"
        onCancel={form.handleDeleteCancel}
        onConfirm={() => {
          markDeleting(form.selectedEntity?.id ?? null);
          form.handleDeleteConfirm();
        }}
        maxWidth="xs"
        destructive={false}
      >
        <Typography>
          Are you sure you want to delete account <strong>{form.selectedEntity?.aws_account_id}</strong> ({form.selectedEntity?.account_name})?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action cannot be undone.
        </Typography>
      </DeleteConfirmDialog>
    </Dialog>
  );
});

AwsAccountManagementModal.displayName = 'AwsAccountManagementModal';

export default AwsAccountManagementModal;
